// Strengths/weaknesses callouts: picked agent vs cohort fail rate.
// Diverging bar -- strengths grow right (green), weaknesses left
// (red), zero line = the cohort average.

import React, { useContext, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { isEmpty, isNil } from 'lodash'

import { Card } from '@/components/cards/Card'
import { apiService } from '@/api'

import { CHART } from '@/components/charts/chartPalette'
import { EntityClickContext } from '../../helpers/dashboardContexts'
import { buildDashboardParams, useDashboardOrgId } from '../../helpers/dashboardParams'
import { useDashboardFetch } from '../../hooks/useDashboardFetch'
import { strengthsWeaknessesFrom, usePickedEntity } from '../../helpers/cohort'
import { escapeHtml } from '../../helpers/format'
import { ChartWidgetCard, axisTooltip, hBarBase } from '../chartWidget'

export const StrengthsWeaknessesWidget = ({ scope }) => {
  // Plain filters (NOT useFiltersForPickedScope): the picker's dim must
  // stay stripped so by_criteria returns the org-wide cohort baseline.
  const filters = useSelector((state) => state.qaCopilot.filters)
  const orgId = useDashboardOrgId()
  const pickedEntity = usePickedEntity(scope)
  const clickEntity = useContext(EntityClickContext)

  const url = orgId
    ? `${apiService.scorecard}/scoring/scores/clickhouse/aggregate/callCount/by_criteria?${buildDashboardParams(filters, orgId).toString()}`
    : null
  const { data, loading } = useDashboardFetch(url, [orgId, scope, filters])

  const { strengths, weaknesses, criterionByName } = useMemo(() => {
    const criteria = Array.isArray(data) ? data : data?.items || []
    if (isEmpty(criteria) || !pickedEntity || scope !== 'agent') {
      return { strengths: [], weaknesses: [], criterionByName: {} }
    }
    const userId = String(pickedEntity.value)
    const entityRates = {}
    const cohortRates = {}
    const byName = {}
    criteria.forEach((criterion) => {
      const name = criterion.criteria_name || criterion.name
      if (!name) {
        return
      }
      byName[name] = criterion
      const cohortFailPct = parseFloat(criterion.failing_percent ?? 0)
      cohortRates[name] = (100 - cohortFailPct) / 100
      const infractor = (criterion.top_infractors || []).find(
        (row) => String(row.user_id) === userId
      )
      if (infractor && !isNil(infractor.personal_failure_rate)) {
        entityRates[name] = 1 - parseFloat(infractor.personal_failure_rate) / 100
      }
    })
    const sw = strengthsWeaknessesFrom(entityRates, cohortRates, { strengthsN: 3, weaknessesN: 3 })
    return { ...sw, criterionByName: byName }
  }, [data, pickedEntity, scope])

  // Strengths first (biggest positive delta on top), then weaknesses.
  const chartRows = useMemo(
    () =>
      [...strengths, ...weaknesses].map((row) => ({
        name: row.id,
        deltaPoints: row.delta * 100,
        entityPct: row.entity * 100,
        cohortPct: row.cohort * 100,
      })),
    [strengths, weaknesses]
  )

  const option = useMemo(() => {
    if (isEmpty(chartRows)) {
      return {}
    }
    return {
      tooltip: axisTooltip((params) => {
        const row = params?.[0]?.data?.row
        if (!row) {
          return ''
        }
        const sign = row.deltaPoints >= 0 ? '+' : ''
        return `${escapeHtml(row.name)}<br/>This agent: ${row.entityPct.toFixed(0)}% pass (avg ${row.cohortPct.toFixed(0)}%)<br/>${sign}${row.deltaPoints.toFixed(0)} points vs avg<br/><em>Click for details</em>`
      }),
      ...hBarBase(
        chartRows.map((row) => row.name),
        {
          left: 150,
          right: 48,
          top: 8,
          xAxis: {
            name: 'Δ points vs avg',
            nameTextStyle: { fontSize: 10 },
            axisLine: { onZero: true },
            axisLabel: {
              fontSize: 10,
              formatter: (value) => (value >= 0 ? `+${value}` : `${value}`),
            },
          },
        }
      ),
      series: [
        {
          type: 'bar',
          cursor: 'pointer',
          barWidth: '60%',
          data: chartRows.map((row) => {
            const criterion = criterionByName[row.name]
            return {
              value: parseFloat(row.deltaPoints.toFixed(1)),
              row,
              // Click payload embedded per point -- handlers read
              // p.data, never closures.
              drill: criterion
                ? {
                    criteria_id: criterion.criteria_id,
                    name: row.name,
                    scorecard_config_sid: criterion.scorecard_config_sid,
                    picked: pickedEntity?.value
                      ? { user_id: pickedEntity.value, full_name: pickedEntity.label }
                      : null,
                  }
                : null,
              itemStyle: {
                color: row.deltaPoints >= 0 ? CHART.SUCCESS : CHART.CRITICAL,
                borderRadius: row.deltaPoints >= 0 ? [0, 3, 3, 0] : [3, 0, 0, 3],
              },
            }
          }),
          label: {
            show: true,
            position: 'right',
            fontSize: 10,
            formatter: ({ value }) => `${value >= 0 ? '+' : ''}${value}pp`,
          },
        },
      ],
    }
  }, [chartRows, criterionByName, pickedEntity])

  // Custom handler: builds agentContext from drill.picked so the
  // criterion drawer opens scoped to the picked agent.
  const events = useMemo(
    () => ({
      click: (params) => {
        const drill = params?.data?.drill
        if (!drill?.criteria_id || !clickEntity) {
          return
        }
        clickEntity({
          type: 'criterion',
          payload: {
            criteria_id: drill.criteria_id,
            name: drill.name,
            scorecard_config_sid: drill.scorecard_config_sid,
            agentContext: drill.picked || undefined,
          },
        })
      },
    }),
    [clickEntity]
  )

  if (loading) {
    return (
      <Card>
        <div className="entity-drawer-empty">Loading…</div>
      </Card>
    )
  }
  if (scope !== 'agent') {
    return (
      <Card title="Strengths & weaknesses">
        <div className="entity-drawer-empty">Available on the By Agent tab.</div>
      </Card>
    )
  }
  if (isEmpty(strengths) && isEmpty(weaknesses)) {
    return (
      <Card title="Strengths & weaknesses">
        <div className="entity-drawer-empty">
          This agent isn&apos;t in the top failures for any criterion in this window. Nothing stands
          out vs the average either way.
        </div>
      </Card>
    )
  }

  return (
    <ChartWidgetCard
      title="Strengths & weaknesses vs average"
      info="The criteria where this entity most out- or under-performs the cohort average — its clearest strengths and coaching opportunities."
      hint="Click a criterion to open its drawer"
      option={option}
      events={events}
      height={`${chartRows.length * 36 + 56}px`}
    />
  )
}
